// @flow

/**
 * Prompt Chat Provider
 * Generic context provider for chat state management with reducer
 */

import React, { createContext, useReducer, useMemo, useEffect, useContext, useRef, useCallback } from 'react';
import { promptChatReducer, createInitialState } from './promptChatReducer';
import { DEFAULT_MAX_PROMPT_CHARS, DEFAULT_AUTO_SCROLL_THRESHOLD_PX } from '../constants';
import { DownloadActionsContext } from './DownloadActionsContext';
import { CustomTagsContext } from './CustomTagsContext';
import PromptButton from '../components/PromptButton';
import { DataDisplayTag } from '../components/data-display';
import { AnswerSummary, AnswerDetail } from '../components/answer-blocks';

import type { ChatState, PromptChatAction, PromptChatProviderProps, StreamingHookResult, CustomTagConfig } from '../types';

// Default tags - prompt and data-display are always enabled
const DEFAULT_CUSTOM_TAGS: CustomTagConfig[] = [
  { tagName: 'prompt', component: PromptButton },
  { tagName: 'data-display', component: DataDisplayTag, allowedAttributes: ['id'], block: true },
  { tagName: 'answer-summary', component: AnswerSummary, block: true },
  { tagName: 'answer-detail', component: AnswerDetail, block: true },
];

export const PromptChatStateContext = createContext<?ChatState>(null);
export const PromptChatDispatchContext = createContext<?(action: PromptChatAction) => void>(null);
export const StreamingAdapterContext = createContext<?StreamingHookResult>(null);
export const FeedbackServiceContext = createContext<?any>(null);

/**
 * Inner wrapper component that can use hooks requiring the provider context
 * This allows us to access usePromptChatController for the submitPrompt callback
 */
type CustomTagsWrapperProps = {
  children: React$Node,
  customTags?: CustomTagConfig[],
  promptButtonsDisabled?: boolean,
};

const CustomTagsWrapper = ({ children, customTags, promptButtonsDisabled = false }: CustomTagsWrapperProps): React$Node => {
  // Import hooks inline to avoid circular dependency
  const { usePromptChatController } = require('../hooks/usePromptChatController');
  
  const state = useContext(PromptChatStateContext);
  const { send } = usePromptChatController();
  const isStreaming = state?.isStreaming ?? false;

  const contextValue = useMemo(() => ({
    tags: customTags || DEFAULT_CUSTOM_TAGS,
    submitPrompt: send,
    isStreaming,
    promptButtonsDisabled,
  }), [customTags, send, isStreaming, promptButtonsDisabled]);

  return (
    <CustomTagsContext.Provider value={contextValue}>
      {children}
    </CustomTagsContext.Provider>
  );
};

const PromptChatProvider = ({
  children,
  sessionId: sessionIdProp,
  onSessionIdChange,
  initialAgentText = '',
  suppressIntroMessage = false,
  maxPromptChars = DEFAULT_MAX_PROMPT_CHARS,
  streamingAdapter,
  feedbackService,
  autoScrollThreshold = DEFAULT_AUTO_SCROLL_THRESHOLD_PX,
  onError,
  downloadActions,
  promptButtonsDisabled = false,
}: PromptChatProviderProps) => {
  // Generate session ID if not provided
  const generatedSessionId = useMemo(() => {
    if (sessionIdProp) return sessionIdProp;
    return (
      (window.crypto &&
        window.crypto.randomUUID &&
        window.crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    );
  }, [sessionIdProp]);

  // Initialize state with reducer
  const [state, rawDispatch] = useReducer(
    promptChatReducer,
    createInitialState(generatedSessionId, initialAgentText, suppressIntroMessage)
  );

  // Mount guard: prevent dispatches after unmount (e.g. from async stream callbacks)
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const dispatch = useCallback((action) => {
    if (mountedRef.current) rawDispatch(action);
  }, []);

  // Notify parent when session ID changes
  useEffect(() => {
    if (onSessionIdChange && state.sessionId) {
      onSessionIdChange(state.sessionId);
    }
  }, [state.sessionId, onSessionIdChange]);

  // Notify parent when errors occur
  useEffect(() => {
    if (onError && state.error) {
      onError(state.error);
    }
  }, [state.error, onError]);

  // Create streaming adapter instance
  const adapterInstance = useMemo(() => {
    return streamingAdapter(dispatch);
  }, [streamingAdapter]);

  // Memoize context values
  const stateValue = useMemo(() => ({
    ...state,
    maxPromptChars,
    autoScrollThreshold,
    initialAgentText,
  }), [state, maxPromptChars, autoScrollThreshold, initialAgentText]);

  return (
    <PromptChatStateContext.Provider value={stateValue}>
      <PromptChatDispatchContext.Provider value={dispatch}>
        <StreamingAdapterContext.Provider value={adapterInstance}>
          <FeedbackServiceContext.Provider value={feedbackService || null}>
            <DownloadActionsContext.Provider value={downloadActions || null}>
              <CustomTagsWrapper customTags={DEFAULT_CUSTOM_TAGS} promptButtonsDisabled={promptButtonsDisabled}>
                {children}
              </CustomTagsWrapper>
            </DownloadActionsContext.Provider>
          </FeedbackServiceContext.Provider>
        </StreamingAdapterContext.Provider>
      </PromptChatDispatchContext.Provider>
    </PromptChatStateContext.Provider>
  );
};

export default PromptChatProvider;
